import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, ArrowRight, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { trackGaEvent } from "@/lib/ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const completedCheckoutSessionsRef = useRef(new Set());

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus("error");
      toast.error("Payment verification timed out. Please contact support.");
      return;
    }

    setAttempts(attempt + 1);

    try {
      const response = await axios.get(
        `${API}/payments/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === "paid") {
        if (!completedCheckoutSessionsRef.current.has(sessionId)) {
          trackGaEvent("checkout_completed", {
            session_id: sessionId,
          });
          completedCheckoutSessionsRef.current.add(sessionId);
        }
        setStatus("success");
        toast.success("Payment successful! Your plan is ready and a confirmation email is on its way.");
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        toast.error("Payment session expired");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    } catch (error) {
      console.error("Payment status error:", error);
      // Retry on error
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Card className="opacity-0 animate-fade-in">
          <CardContent className="p-8 text-center">
            {status === "checking" && (
              <>
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
                <h1 className="font-manrope font-bold text-2xl text-foreground mb-2">
                  Verifying Payment
                </h1>
                <p className="text-muted-foreground mb-4">
                  Please wait while we confirm your payment...
                </p>
                <p className="text-sm text-muted-foreground">
                  Attempt {attempts}/5
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="font-manrope font-bold text-2xl text-foreground mb-2">
                  Payment Successful!
                </h1>
                <p className="text-muted-foreground mb-6">
                  Thank you for your purchase. Your personalized Relocation Blueprint is now ready, and we’ve emailed you direct links to the web version and PDF.
                </p>
                <p className="text-sm leading-7 text-muted-foreground mb-6" data-testid="payment-success-refresh-policy-copy">
                  Your paid plan is based on your current assessment and includes one free refresh if you update your answers later.
                </p>
                <Button 
                  data-testid="view-plan-btn"
                  onClick={() => navigate("/plan")}
                  className="rounded-full bg-primary hover:bg-primary/90 px-8"
                >
                  View Your Relocation Blueprint
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h1 className="font-manrope font-bold text-2xl text-foreground mb-2">
                  Payment Issue
                </h1>
                <p className="text-muted-foreground mb-6">
                  We couldn't verify your payment. If you were charged, please contact support.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    data-testid="try-again-btn"
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                    className="rounded-full"
                  >
                    Return to Dashboard
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;
